export type CallsLiveDeviceEvidenceAuthorityDecision =
  | 'authorized'
  | 'rejected';

export type CallsLiveDeviceEvidenceRejectionCode =
  | 'INVALID_EVIDENCE_ID'
  | 'INVALID_SESSION_ID'
  | 'INVALID_DEVICE_ID'
  | 'INVALID_EVENT'
  | 'INVALID_CAPTURED_AT'
  | 'INVALID_RECEIVED_AT'
  | 'SESSION_MISMATCH'
  | 'DUPLICATE_EVIDENCE'
  | 'STALE_EVIDENCE'
  | 'FUTURE_EVIDENCE'
  | 'INVALID_SEQUENCE'
  | 'SEQUENCE_REGRESSION';

export interface CallsLiveDeviceEvidenceCandidate {
  readonly evidenceId: string;
  readonly sessionId: string;
  readonly deviceId: string;
  readonly event: string;
  readonly capturedAt: string;
  readonly receivedAt?: string;
  readonly sequence?: number;
  readonly payloadHash?: string;
  readonly metadata?: Readonly<Record<string, unknown>>;
}

export interface CallsLiveDeviceEvidenceAuthorityContext {
  readonly activeSessionId: string;
  readonly now?: string;
  readonly maximumAgeMs?: number;
  readonly maximumFutureSkewMs?: number;
  readonly seenEvidenceIds?: ReadonlySet<string>;
  readonly lastSequenceByDevice?: ReadonlyMap<string, number>;
}

export interface AuthorizedCallsLiveDeviceEvidence
  extends CallsLiveDeviceEvidenceCandidate {
  readonly authority: 'calls-live-device-evidence-authority';
  readonly authorityVersion: 'CG-021';
  readonly authorizedAt: string;
}

export interface CallsLiveDeviceEvidenceAuthorityResult {
  readonly authority: 'calls-live-device-evidence-authority';
  readonly authorityVersion: 'CG-021';
  readonly decision: CallsLiveDeviceEvidenceAuthorityDecision;
  readonly rejectionCode: CallsLiveDeviceEvidenceRejectionCode | null;
  readonly reason: string;
  readonly evaluatedAt: string;
  readonly evidence: AuthorizedCallsLiveDeviceEvidence | null;
}

export type CallsLiveDeviceEvidenceRegistryWriter<TResult> = (
  evidence: AuthorizedCallsLiveDeviceEvidence,
) => TResult;

const DEFAULT_MAXIMUM_AGE_MS = 5 * 60 * 1000;
const DEFAULT_MAXIMUM_FUTURE_SKEW_MS = 30 * 1000;

function isNonEmpty(value: string): boolean {
  return value.trim().length > 0;
}

function parseTimestamp(value: string): number | null {
  const parsed = Date.parse(value);

  return Number.isFinite(parsed) ? parsed : null;
}

function reject(
  code: CallsLiveDeviceEvidenceRejectionCode,
  reason: string,
  evaluatedAt: string,
): CallsLiveDeviceEvidenceAuthorityResult {
  return Object.freeze({
    authority: 'calls-live-device-evidence-authority',
    authorityVersion: 'CG-021',
    decision: 'rejected',
    rejectionCode: code,
    reason,
    evaluatedAt,
    evidence: null,
  });
}

/**
 * Validates whether a live-device evidence candidate is eligible to enter the
 * Calls evidence registry.
 *
 * This authority validates identity, session ownership, freshness,
 * duplication, timestamp integrity, and sequence ordering. Cryptographic
 * device attestation remains a separate native-security responsibility.
 */
export function authorizeCallsLiveDeviceEvidence(
  candidate: CallsLiveDeviceEvidenceCandidate,
  context: CallsLiveDeviceEvidenceAuthorityContext,
): CallsLiveDeviceEvidenceAuthorityResult {
  const evaluatedAt = context.now ?? new Date().toISOString();
  const evaluatedAtMs = parseTimestamp(evaluatedAt);

  if (evaluatedAtMs === null) {
    throw new Error(
      'CallsLiveDeviceEvidenceAuthority received an invalid authority clock.',
    );
  }

  if (!isNonEmpty(candidate.evidenceId)) {
    return reject(
      'INVALID_EVIDENCE_ID',
      'Evidence requires a non-empty evidenceId.',
      evaluatedAt,
    );
  }

  if (!isNonEmpty(candidate.sessionId)) {
    return reject(
      'INVALID_SESSION_ID',
      'Evidence requires a non-empty sessionId.',
      evaluatedAt,
    );
  }

  if (!isNonEmpty(candidate.deviceId)) {
    return reject(
      'INVALID_DEVICE_ID',
      'Evidence requires a non-empty deviceId.',
      evaluatedAt,
    );
  }

  if (!isNonEmpty(candidate.event)) {
    return reject(
      'INVALID_EVENT',
      'Evidence requires a non-empty runtime event.',
      evaluatedAt,
    );
  }

  const capturedAtMs = parseTimestamp(candidate.capturedAt);

  if (capturedAtMs === null) {
    return reject(
      'INVALID_CAPTURED_AT',
      'Evidence capturedAt must be a valid timestamp.',
      evaluatedAt,
    );
  }

  if (
    candidate.receivedAt !== undefined &&
    parseTimestamp(candidate.receivedAt) === null
  ) {
    return reject(
      'INVALID_RECEIVED_AT',
      'Evidence receivedAt must be a valid timestamp when supplied.',
      evaluatedAt,
    );
  }

  if (
    !isNonEmpty(context.activeSessionId) ||
    candidate.sessionId !== context.activeSessionId
  ) {
    return reject(
      'SESSION_MISMATCH',
      'Evidence does not belong to the active Calls session.',
      evaluatedAt,
    );
  }

  if (context.seenEvidenceIds?.has(candidate.evidenceId)) {
    return reject(
      'DUPLICATE_EVIDENCE',
      'Evidence with this evidenceId has already been registered.',
      evaluatedAt,
    );
  }

  const maximumAgeMs =
    context.maximumAgeMs ?? DEFAULT_MAXIMUM_AGE_MS;
  const maximumFutureSkewMs =
    context.maximumFutureSkewMs ?? DEFAULT_MAXIMUM_FUTURE_SKEW_MS;

  if (evaluatedAtMs - capturedAtMs > maximumAgeMs) {
    return reject(
      'STALE_EVIDENCE',
      'Evidence is older than the permitted live-device evidence window.',
      evaluatedAt,
    );
  }

  if (capturedAtMs - evaluatedAtMs > maximumFutureSkewMs) {
    return reject(
      'FUTURE_EVIDENCE',
      'Evidence timestamp exceeds the permitted future clock skew.',
      evaluatedAt,
    );
  }

  if (
    candidate.sequence !== undefined &&
    (!Number.isSafeInteger(candidate.sequence) || candidate.sequence < 0)
  ) {
    return reject(
      'INVALID_SEQUENCE',
      'Evidence sequence must be a non-negative safe integer.',
      evaluatedAt,
    );
  }

  const lastSequence =
    context.lastSequenceByDevice?.get(candidate.deviceId);

  if (
    candidate.sequence !== undefined &&
    lastSequence !== undefined &&
    candidate.sequence <= lastSequence
  ) {
    return reject(
      'SEQUENCE_REGRESSION',
      'Evidence sequence must advance beyond the last accepted device sequence.',
      evaluatedAt,
    );
  }

  const evidence: AuthorizedCallsLiveDeviceEvidence = Object.freeze({
    ...candidate,
    metadata: candidate.metadata
      ? Object.freeze({ ...candidate.metadata })
      : undefined,
    authority: 'calls-live-device-evidence-authority',
    authorityVersion: 'CG-021',
    authorizedAt: evaluatedAt,
  });

  return Object.freeze({
    authority: 'calls-live-device-evidence-authority',
    authorityVersion: 'CG-021',
    decision: 'authorized',
    rejectionCode: null,
    reason:
      'Live-device evidence passed Calls authority validation and is eligible for registry admission.',
    evaluatedAt,
    evidence,
  });
}

/**
 * Enforces Authority -> Registry ordering.
 *
 * The registry writer is invoked only after CG-021 authorizes the evidence.
 * Rejected evidence never reaches the supplied registry writer.
 */
export function authorizeAndRegisterCallsLiveDeviceEvidence<TResult>(
  candidate: CallsLiveDeviceEvidenceCandidate,
  context: CallsLiveDeviceEvidenceAuthorityContext,
  registryWriter: CallsLiveDeviceEvidenceRegistryWriter<TResult>,
):
  | Readonly<{
      authorityResult: CallsLiveDeviceEvidenceAuthorityResult;
      registered: true;
      registryResult: TResult;
    }>
  | Readonly<{
      authorityResult: CallsLiveDeviceEvidenceAuthorityResult;
      registered: false;
      registryResult: null;
    }> {
  const authorityResult = authorizeCallsLiveDeviceEvidence(
    candidate,
    context,
  );

  if (
    authorityResult.decision !== 'authorized' ||
    authorityResult.evidence === null
  ) {
    return Object.freeze({
      authorityResult,
      registered: false,
      registryResult: null,
    });
  }

  const registryResult = registryWriter(authorityResult.evidence);

  return Object.freeze({
    authorityResult,
    registered: true,
    registryResult,
  });
}
